/**
 * Octopus IoT Platform - Alerts API Service
 * Alert Center: API calls for alerts, alert rules, and notification settings
 * 
 * Reference: docs/OCTOPUS_ALERTCENTER_DESIGN_DOC.md
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const alertsApi = {
  // ==================== ALERTS ====================
  
  /**
   * Get all alerts with optional filters
   * @param {object} params - { status?, equipment_id?, limit? }
   */
  getAlerts: (params = {}) => 
    axios.get(`${API}/alerts`, { params }),
  
  /**
   * Get count of active alerts
   */
  getActiveCount: () => 
    axios.get(`${API}/alerts/active/count`),
  
  /**
   * Acknowledge an alert (mark as seen)
   * @param {string} alertId - Alert UUID
   */
  acknowledgeAlert: (alertId) => 
    axios.put(`${API}/alerts/${alertId}/acknowledge`),
  
  /**
   * Resolve an alert (mark as fixed)
   * @param {string} alertId - Alert UUID
   */
  resolveAlert: (alertId) => 
    axios.put(`${API}/alerts/${alertId}/resolve`),

  /**
   * Acknowledge alerts in bulk with optional filters
   * @param {object} params - { status?, equipment_id?, alert_type?, severity? }
   */
  acknowledgeAllAlerts: (params = {}) =>
    axios.put(`${API}/alerts/acknowledge-all`, null, { params }),

  /**
   * Resolve alerts in bulk with optional filters
   * @param {object} params - { status?, equipment_id?, alert_type?, severity? }
   */
  resolveAllAlerts: (params = {}) =>
    axios.put(`${API}/alerts/resolve-all`, null, { params }),

  /**
   * Ignore a pending detected source. This is the explicit resolution path for
   * persistent source_detected alerts.
   */
  ignoreSourceGroup: (equipmentId, sourceGroupId) =>
    axios.post(`${API}/equipments/${equipmentId}/source-groups/${sourceGroupId}/ignore`),

  // ==================== ALERT RULES ====================
  
  /**
   * Get all alert rules, optionally filtered by equipment
   * @param {string} equipmentId - Optional equipment UUID
   */
  getRules: (equipmentId) => {
    const params = equipmentId ? { equipment_id: equipmentId } : {};
    return axios.get(`${API}/alert-rules`, { params });
  },
  
  /**
   * Get a specific alert rule
   * @param {string} ruleId - Rule UUID
   */
  getRule: (ruleId) => 
    axios.get(`${API}/alert-rules/${ruleId}`),
  
  /**
   * Create a new alert rule
   * @param {object} data - Rule data
   */
  createRule: (data) => 
    axios.post(`${API}/alert-rules`, data),
  
  /**
   * Update an alert rule
   * @param {string} ruleId - Rule UUID
   * @param {object} data - Rule data to update
   */
  updateRule: (ruleId, data) => 
    axios.put(`${API}/alert-rules/${ruleId}`, data),
  
  /**
   * Delete an alert rule
   * @param {string} ruleId - Rule UUID
   */
  deleteRule: (ruleId) => 
    axios.delete(`${API}/alert-rules/${ruleId}`),

  // ==================== SIGNALK PATHS ====================
  
  /**
   * Get all SignalK paths available for creating alert rules
   */
  getSignalkPaths: () => 
    axios.get(`${API}/alerts/signalk-paths`),
  
  /**
   * Get available SignalK paths for a specific equipment
   * @param {string} equipmentId - Equipment UUID
   */
  getEquipmentPaths: (equipmentId) => 
    axios.get(`${API}/equipments/${equipmentId}/alerts/available-paths`),

  // ==================== NOTIFICATION SETTINGS ====================
  
  /**
   * Get notification settings for alerts (Email only)
   * WhatsApp is configured via Evolution settings (/api/settings/evolution)
   */
  getNotificationSettings: () => 
    axios.get(`${API}/notification-settings`),
  
  /**
   * Update notification settings (Email only)
   * @param {object} settings - { email_enabled, smtp_host, smtp_port, smtp_email, smtp_password? }
   * WhatsApp is configured via Evolution settings (/api/settings/evolution)
   */
  updateNotificationSettings: (settings) =>
    axios.put(`${API}/notification-settings`, settings),

  /**
   * Test email notification settings
   * Sends a test email to the current user
   */
  testEmailNotification: () =>
    axios.post(`${API}/notification-settings/test-email`),

  // NOTE: testWhatsAppNotification lives in /api/settings/evolution/test-message

  // NOTE (Fase 1000, AD-01): o roteamento global de WhatsApp da Fase 999.3
  // (lista única + kill-switch) foi removido — destinatários agora são por
  // equipamento/categoria (ver alertConfigApi.js) ou por regra (campos
  // notification_phones/notification_emails do próprio alert_rule).

  // ==================== EQUIPMENTS ====================
  
  /**
   * Get all equipments (for filtering alerts)
   */
  getEquipments: () => 
    axios.get(`${API}/equipments`),
};

export default alertsApi;
